import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Zap, Shield, AlertTriangle, CheckCircle2, XCircle, Clock, Loader2,
  RefreshCw, ChevronRight, Users, ArrowUpRight, MessageSquare, Play,
  Settings, History, ListChecks, Bell, BarChart3, TrendingUp, Target, Activity,
  Mail, Send, Download, FileText, Calendar, Phone, Smartphone
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, PieChart, Pie, Legend
} from 'recharts';

const TIER_COLORS = { L1: 'bg-blue-100 text-blue-700', L2: 'bg-amber-100 text-amber-700', L3: 'bg-orange-100 text-orange-700', L4: 'bg-red-100 text-red-700' };
const TIER_NAMES = { L1: 'Auto', L2: 'Human', L3: 'Specialist', L4: 'Management' };
const STATUS_COLORS = { open: 'bg-red-100 text-red-700', assigned: 'bg-blue-100 text-blue-700', resolved: 'bg-emerald-100 text-emerald-700', sla_breached: 'bg-red-600 text-white', re_escalated: 'bg-purple-100 text-purple-700' };

export default function RemediationHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Remediation');
  const [view, setView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [escalations, setEscalations] = useState(null);
  const [history, setHistory] = useState(null);
  const [testTitle, setTestTitle] = useState('');
  const [testDesc, setTestDesc] = useState('');
  const [testPriority, setTestPriority] = useState('medium');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState({ rule_type: 'it', name: '', category: '', keywords: '', auto_response: '', script: '', confidence: 'medium', alert_types: '', auto_action: '', ioc_pattern: '', action: 'alert', severity_threshold: 'high' });
  const [customRules, setCustomRules] = useState([]);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportSchedule, setReportSchedule] = useState(null);
  const [reportLogs, setReportLogs] = useState([]);
  const [reportRecipients, setReportRecipients] = useState('');
  const [reportDays, setReportDays] = useState(7);
  const [sendingReport, setSendingReport] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [smsConfig, setSmsConfig] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsLogs, setSmsLogs] = useState([]);
  const [testPhone, setTestPhone] = useState('');
  const [sendingSms, setSendingSms] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, rulesRes, appRes, escRes] = await Promise.all([
        api.get('/api/remediation/stats'),
        api.get('/api/remediation/rules'),
        api.get('/api/remediation/approval-queue'),
        api.get('/api/remediation/escalations'),
      ]);
      setStats(statsRes.data);
      setRules(rulesRes.data);
      setApprovals(appRes.data.queue || []);
      setEscalations(escRes.data);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadHistory = async () => {
    const { data } = await api.get('/api/remediation/history');
    setHistory(data);
  };

  const loadCustomRules = async () => {
    const { data } = await api.get('/api/remediation/rules/custom');
    setCustomRules(data.rules || []);
  };

  const saveCustomRule = async () => {
    if (!ruleForm.name) { toast.error('Rule name required'); return; }
    try {
      const payload = { ...ruleForm };
      if (ruleForm.rule_type === 'it') {
        payload.keywords = ruleForm.keywords.split(',').map(k => k.trim()).filter(Boolean);
      } else if (ruleForm.rule_type === 'scada') {
        payload.alert_types = ruleForm.alert_types.split(',').map(a => a.trim()).filter(Boolean);
      }
      await api.post('/api/remediation/rules/custom', payload);
      toast.success('Custom rule created!');
      setShowRuleForm(false);
      setRuleForm({ rule_type: 'it', name: '', category: '', keywords: '', auto_response: '', script: '', confidence: 'medium', alert_types: '', auto_action: '', ioc_pattern: '', action: 'alert', severity_threshold: 'high' });
      fetchData();
      loadCustomRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteCustomRule = async (id) => {
    try {
      await api.delete(`/api/remediation/rules/custom/${id}`);
      toast.success('Rule deleted');
      fetchData();
      loadCustomRules();
    } catch { toast.error('Delete failed'); }
  };

  const loadWebhookConfig = async () => {
    try {
      const [cfgRes, logRes] = await Promise.all([
        api.get('/api/remediation/webhooks/config'),
        api.get('/api/remediation/webhooks/logs'),
      ]);
      setWebhookConfig(cfgRes.data.config || {});
      setWebhookLogs(logRes.data.logs || []);
    } catch (e) { /* handled */ }
  };

  const saveWebhookConfig = async () => {
    try {
      await api.put('/api/remediation/webhooks/config', webhookConfig);
      toast.success('Webhook config saved');
    } catch { toast.error('Save failed'); }
  };

  const testWebhook = async () => {
    try {
      const { data } = await api.post('/api/remediation/webhooks/test', { channel: 'all' });
      toast.success(`Sent to: ${data.sent_to?.join(', ') || 'No channels configured'}`);
      loadWebhookConfig();
    } catch { toast.error('Test failed'); }
  };

  const loadAnalytics = async (days = 30) => {
    try {
      const { data } = await api.get(`/api/remediation/analytics?days=${days}`);
      setAnalytics(data);
    } catch { toast.error('Analytics load failed'); }
  };

  const loadReports = async () => {
    try {
      const [schedRes, logRes, statusRes] = await Promise.all([
        api.get('/api/reports/schedule'),
        api.get('/api/reports/history'),
        api.get('/api/reports/email-status'),
      ]);
      setReportSchedule(schedRes.data.config || {});
      setReportLogs(logRes.data.logs || []);
      setEmailStatus(statusRes.data);
      setReportRecipients((schedRes.data.config?.recipients || []).join(', '));
    } catch (e) { /* handled */ }
  };

  const previewReport = async () => {
    try {
      const { data } = await api.get(`/api/reports/preview?days=${reportDays}`);
      setReportPreview(data.html);
    } catch { toast.error('Preview failed'); }
  };

  const sendReport = async () => {
    const recipients = reportRecipients.split(',').map(r => r.trim()).filter(Boolean);
    if (!recipients.length) { toast.error('Add at least one recipient email'); return; }
    setSendingReport(true);
    try {
      const { data } = await api.post('/api/reports/send', { recipients, days: reportDays });
      if (data.sent) {
        toast.success(`Report sent to ${recipients.join(', ')}`);
      } else {
        toast.error(data.detail || 'Send failed — check SendGrid config');
      }
      loadReports();
    } catch { toast.error('Send failed'); } finally { setSendingReport(false); }
  };

  const saveSchedule = async () => {
    try {
      const recipients = reportRecipients.split(',').map(r => r.trim()).filter(Boolean);
      await api.put('/api/reports/schedule', { ...reportSchedule, recipients });
      toast.success('Schedule saved');
    } catch { toast.error('Save failed'); }
  };

  const downloadReport = () => {
    if (!reportPreview) return;
    const blob = new Blob([reportPreview], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `techops-report-${new Date().toISOString().slice(0, 10)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadSmsData = async () => {
    try {
      const [statusRes, configRes, logRes] = await Promise.all([
        api.get('/api/sms/status'),
        api.get('/api/sms/config'),
        api.get('/api/sms/logs'),
      ]);
      setSmsStatus(statusRes.data);
      setSmsConfig(configRes.data.config || {});
      setSmsLogs(logRes.data.logs || []);
    } catch (e) { /* handled */ }
  };

  const saveSmsConfig = async () => {
    try {
      await api.put('/api/sms/config', smsConfig);
      toast.success('SMS config saved');
    } catch { toast.error('Save failed'); }
  };

  const testSms = async () => {
    if (!testPhone) { toast.error('Enter a phone number'); return; }
    setSendingSms(true);
    try {
      const { data } = await api.post('/api/sms/test', { phone: testPhone });
      if (data.success) {
        toast.success(`Test SMS sent to ${testPhone}`);
      } else {
        toast.error(data.detail || 'Test failed');
      }
      loadSmsData();
    } catch { toast.error('Test failed'); } finally { setSendingSms(false); }
  };

  const runSmartResolve = async () => {
    if (!testTitle) { toast.error('Enter a ticket title'); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/api/remediation/smart-resolve', { title: testTitle, description: testDesc, priority: testPriority });
      setTestResult(data);
      toast.success(`Result: ${data.action}`);
      fetchData();
    } catch { toast.error('Test failed'); } finally { setTesting(false); }
  };

  const decideApproval = async (id, decision, note = '') => {
    try {
      await api.put(`/api/remediation/approval-queue/${id}`, { decision, note });
      toast.success(`Action ${decision}`);
      fetchData();
    } catch { toast.error('Decision failed'); }
  };

  const updateEscalation = async (id, action, extra = {}) => {
    try {
      await api.put(`/api/remediation/escalations/${id}`, { action, ...extra });
      toast.success(`Escalation ${action}d`);
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const checkSla = async () => {
    try {
      const { data } = await api.post('/api/remediation/check-sla');
      toast.info(`Checked ${data.checked}, Breached: ${data.breached}, Auto-escalated: ${data.auto_escalated}`);
      fetchData();
    } catch { toast.error('SLA check failed'); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  const pendingApprovals = approvals.filter(a => a.status === 'pending_approval');
  const openEscalations = escalations?.escalations?.filter(e => ['open', 'assigned'].includes(e.status)) || [];

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="remediation-hub">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><Zap className="w-6 h-6 text-[#D4A843]" /> Auto-Remediation Engine</h1>
              <p className="text-sm text-gray-500 mt-1">Intelligent auto-resolve, approval queue, and escalation management</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={checkSla} className="rounded-lg text-xs" data-testid="check-sla"><Clock className="w-3.5 h-3.5 mr-1" /> Check SLA</Button>
              <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 pb-0 overflow-x-auto">
            {[
              { k: 'overview', l: 'Overview', icon: Zap },
              { k: 'approvals', l: `Approvals (${pendingApprovals.length})`, icon: ListChecks },
              { k: 'escalations', l: `Escalations (${openEscalations.length})`, icon: ArrowUpRight },
              { k: 'rules', l: 'Rules', icon: Settings },
              { k: 'webhooks', l: 'Webhooks', icon: Bell },
              { k: 'sms', l: 'SMS', icon: Smartphone },
              { k: 'test', l: 'Test Engine', icon: Play },
              { k: 'analytics', l: 'Analytics', icon: BarChart3 },
              { k: 'reports', l: 'Reports', icon: Mail },
              { k: 'history', l: 'History', icon: History },
            ].map(t => (
              <button key={t.k} onClick={() => { setView(t.k); if (t.k === 'history' && !history) loadHistory(); if (t.k === 'rules') loadCustomRules(); if (t.k === 'webhooks') loadWebhookConfig(); if (t.k === 'analytics' && !analytics) loadAnalytics(analyticsDays); if (t.k === 'reports' && !reportSchedule) loadReports(); if (t.k === 'sms' && !smsConfig) loadSmsData(); }} className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`rem-tab-${t.k}`}>
                <t.icon className="w-3.5 h-3.5" />{t.l}
              </button>
            ))}
          </div>

          {/* OVERVIEW */}
          {view === 'overview' && stats && (
            <div className="space-y-4" data-testid="rem-overview">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-gray-900">{stats.rules_active}</p><p className="text-[10px] text-gray-500">Active Rules</p></div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-emerald-700">{stats.it_auto_resolved}</p><p className="text-[10px] text-emerald-600">IT Auto-Resolved</p></div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-blue-700">{stats.scada_auto_remediated}</p><p className="text-[10px] text-blue-600">SCADA Remediated</p></div>
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-violet-700">{stats.sysmon_auto_responded}</p><p className="text-[10px] text-violet-600">Sysmon Responded</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center hover-lift"><p className="text-2xl font-bold text-amber-700">{pendingApprovals.length}</p><p className="text-[10px] text-amber-600">Pending Approvals</p></div>
                <div className={`rounded-lg p-4 text-center hover-lift ${openEscalations.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}><p className={`text-2xl font-bold ${openEscalations.length > 0 ? 'text-red-700' : 'text-gray-900'}`}>{openEscalations.length}</p><p className="text-[10px] text-gray-500">Open Escalations</p></div>
              </div>

              {/* Decision Flow Diagram */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Smart-Resolve Decision Flow</h3>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="px-3 py-2 bg-gray-100 rounded-lg font-medium">Ticket In</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="px-3 py-2 bg-red-100 text-red-700 rounded-lg font-medium">Security?</span>
                  <span className="text-red-500 font-bold">L4</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium">Critical?</span>
                  <span className="text-orange-500 font-bold">L3</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-medium">Rule Match (High)</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg font-medium">Rule Match (Med)</span>
                  <span className="text-amber-500 font-bold">Queue</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="px-3 py-2 bg-violet-100 text-violet-700 rounded-lg font-medium">AI Analysis</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium">L2 Fallback</span>
                </div>
              </div>

              {/* Quick Glance: Pending Approvals */}
              {pendingApprovals.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1"><Bell className="w-3.5 h-3.5" /> Pending Approvals ({pendingApprovals.length})</h3>
                  {pendingApprovals.slice(0, 3).map(a => (
                    <div key={a.id} className="flex items-center justify-between py-2 border-b border-amber-200/50 last:border-0">
                      <div><p className="text-xs font-medium text-gray-900">{a.category}</p><p className="text-[10px] text-gray-500">{a.proposed_action?.slice(0, 80)}...</p></div>
                      <div className="flex gap-1">
                        <Button size="sm" className="bg-emerald-600 text-white rounded h-7 px-2 text-[10px]" onClick={() => decideApproval(a.id, 'approved')} data-testid={`approve-${a.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="text-red-500 rounded h-7 px-2 text-[10px]" onClick={() => decideApproval(a.id, 'rejected', 'Rejected from overview')}>Reject</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* APPROVALS */}
          {view === 'approvals' && (
            <div className="space-y-3" data-testid="rem-approvals">
              {approvals.length === 0 && <p className="text-center py-12 text-gray-400 bg-white rounded-lg border border-gray-200">No items in approval queue</p>}
              {approvals.map(a => (
                <div key={a.id} className={`bg-white border rounded-lg p-5 ${a.status === 'pending_approval' ? 'border-amber-300' : 'border-gray-200'}`} data-testid={`approval-${a.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${a.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' : a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : a.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{a.status?.replace(/_/g, ' ')}</span>
                        <span className="text-[10px] text-gray-400">{a.category}</span>
                        <span className={`text-[10px] font-bold ${a.confidence === 'high' ? 'text-emerald-600' : a.confidence === 'medium' ? 'text-amber-600' : 'text-red-600'}`}>{a.confidence} confidence</span>
                      </div>
                      <p className="text-sm text-gray-900">{a.proposed_action}</p>
                      {a.proposed_script && <pre className="mt-2 text-[10px] bg-gray-900 text-emerald-400 p-3 rounded-lg overflow-x-auto max-h-24 font-mono">{a.proposed_script}</pre>}
                    </div>
                    <span className="text-[10px] text-gray-400">{new Date(a.requested_at).toLocaleString()}</span>
                  </div>
                  {a.status === 'pending_approval' && (
                    <div className="flex gap-2 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={() => decideApproval(a.id, 'approved')} data-testid={`approve-action-${a.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Approve & Execute</Button>
                      <Button size="sm" variant="outline" className="text-red-500 rounded-lg text-xs" onClick={() => decideApproval(a.id, 'rejected', 'Manually rejected')} data-testid={`reject-action-${a.id}`}><XCircle className="w-3 h-3 mr-1" /> Reject & Escalate</Button>
                    </div>
                  )}
                  {a.approver_name && <p className="text-[10px] text-gray-400 mt-2">{a.status === 'approved' ? 'Approved' : 'Rejected'} by {a.approver_name} at {new Date(a.decision_at).toLocaleString()}{a.decision_note && ` — "${a.decision_note}"`}</p>}
                </div>
              ))}
            </div>
          )}

          {/* ESCALATIONS */}
          {view === 'escalations' && escalations && (
            <div className="space-y-4" data-testid="rem-escalations">
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(TIER_NAMES).map(([tier, name]) => {
                  const count = escalations.escalations?.filter(e => e.to_tier === tier && ['open', 'assigned'].includes(e.status)).length || 0;
                  return (
                    <div key={tier} className={`border rounded-lg p-4 text-center ${count > 0 ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'}`}>
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${TIER_COLORS[tier]}`}>{tier}</span>
                      <p className="text-lg font-bold text-gray-900 mt-1">{count}</p>
                      <p className="text-[10px] text-gray-500">{name}</p>
                    </div>
                  );
                })}
              </div>

              {escalations.sla_breached > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
                  <p className="text-xs text-red-700 font-bold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {escalations.sla_breached} escalation(s) have breached SLA</p>
                  <Button size="sm" onClick={checkSla} className="bg-red-600 text-white rounded-lg text-[10px]">Auto-Escalate Breached</Button>
                </div>
              )}

              <div className="space-y-3">
                {escalations.escalations?.map(e => (
                  <div key={e.id} className={`bg-white border rounded-lg p-4 ${e.sla_breached ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`} data-testid={`escalation-${e.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${TIER_COLORS[e.to_tier]}`}>{e.to_tier} {TIER_NAMES[e.to_tier]}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-600'}`}>{e.status?.replace(/_/g, ' ')}</span>
                        {e.sla_breached && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white animate-pulse">SLA BREACHED</span>}
                      </div>
                      <div className="text-right">
                        {e.sla_remaining_min != null && !e.sla_breached && <p className="text-[10px] text-gray-500"><Clock className="w-3 h-3 inline" /> {e.sla_remaining_min}min left</p>}
                        <p className="text-[10px] text-gray-400">{new Date(e.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2"><span className="font-bold">Trigger:</span> {e.trigger?.replace(/_/g, ' ')}</p>
                    {e.context?.title && <p className="text-xs text-gray-500 mt-0.5">{e.context.title}</p>}
                    {e.assigned_name && <p className="text-[10px] text-[#D4A843] mt-1">Assigned to: {e.assigned_name}</p>}

                    {e.notes?.length > 0 && (
                      <div className="mt-2 space-y-1">{e.notes.map((n, i) => (
                        <p key={`item-${i}`} className="text-[10px] text-gray-500 bg-gray-50 p-2 rounded"><span className="font-bold">{n.author}:</span> {n.text}</p>
                      ))}</div>
                    )}

                    {['open', 'assigned'].includes(e.status) && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                        {e.status === 'open' && <Button size="sm" className="bg-[#D4A843] text-[#0A0A0A] font-semibold rounded-lg text-[10px]" onClick={() => updateEscalation(e.id, 'assign', { assigned_to: user?.id, assigned_name: user?.name })} data-testid={`assign-${e.id}`}><Users className="w-3 h-3 mr-1" /> Assign to Me</Button>}
                        <Button size="sm" className="bg-emerald-600 text-white rounded-lg text-[10px]" onClick={() => { const res = prompt('Resolution note:'); if (res) updateEscalation(e.id, 'resolve', { resolution: res }); }} data-testid={`resolve-${e.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Resolve</Button>
                        <Button size="sm" variant="outline" className="text-orange-500 rounded-lg text-[10px]" onClick={() => { const nextTier = { L2: 'L3', L3: 'L4' }[e.to_tier]; if (nextTier) updateEscalation(e.id, 'escalate', { to_tier: nextTier, reason: 'Needs higher-level attention' }); else toast.error('Already at highest tier'); }} data-testid={`reescalate-${e.id}`}><ArrowUpRight className="w-3 h-3 mr-1" /> Escalate</Button>
                        <Button size="sm" variant="outline" className="text-gray-500 rounded-lg text-[10px]" onClick={() => { const note = prompt('Add note:'); if (note) updateEscalation(e.id, 'add_note', { note }); }}><MessageSquare className="w-3 h-3 mr-1" /> Note</Button>
                      </div>
                    )}
                  </div>
                ))}
                {(!escalations.escalations || escalations.escalations.length === 0) && <p className="text-center py-8 text-gray-400 bg-white rounded-lg border border-gray-200">No escalations</p>}
              </div>
            </div>
          )}

          {/* RULES */}
          {view === 'rules' && rules && (
            <div className="space-y-4" data-testid="rem-rules">
              <div className="flex items-center justify-between">
                <div className="grid grid-cols-4 gap-4 flex-1">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[#D4A843]">{rules.total}</p><p className="text-[10px] text-gray-500">Total Rules</p></div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-gray-600">{rules.builtin_count}</p><p className="text-[10px] text-gray-500">Built-in</p></div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[#D4A843]">{rules.custom_count}</p><p className="text-[10px] text-gray-500">Custom</p></div>
                  <div className="flex items-center justify-center"><Button onClick={() => setShowRuleForm(!showRuleForm)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="create-rule-btn"><Settings className="w-3.5 h-3.5 mr-1" /> {showRuleForm ? 'Cancel' : 'Create Rule'}</Button></div>
                </div>
              </div>

              {/* Rule Builder Form */}
              {showRuleForm && (
                <div className="bg-white border-2 border-[#D4A843]/30 rounded-lg p-5 animate-fade-in" data-testid="rule-form">
                  <h3 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-3">New Custom Rule</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div><label className="text-xs text-gray-600">Rule Name *</label><Input value={ruleForm.name} onChange={e => setRuleForm({...ruleForm, name: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g. Teams app crash fix" data-testid="rule-name" /></div>
                    <div><label className="text-xs text-gray-600">Type</label><select value={ruleForm.rule_type} onChange={e => setRuleForm({...ruleForm, rule_type: e.target.value})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="rule-type"><option value="it">IT Support</option><option value="scada">SCADA/OT</option><option value="sysmon">Sysmon/Security</option></select></div>
                  </div>

                  {ruleForm.rule_type === 'it' && (
                    <div className="space-y-3">
                      <div><label className="text-xs text-gray-600">Category</label><Input value={ruleForm.category} onChange={e => setRuleForm({...ruleForm, category: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g. Collaboration Tools" data-testid="rule-category" /></div>
                      <div><label className="text-xs text-gray-600">Keywords (comma separated) *</label><Input value={ruleForm.keywords} onChange={e => setRuleForm({...ruleForm, keywords: e.target.value})} className="mt-1 rounded-lg" placeholder="teams crash, teams not working, teams freezing" data-testid="rule-keywords" /></div>
                      <div><label className="text-xs text-gray-600">Auto-Response Message *</label><Input value={ruleForm.auto_response} onChange={e => setRuleForm({...ruleForm, auto_response: e.target.value})} className="mt-1 rounded-lg" placeholder="Teams cache has been cleared and the app restarted..." data-testid="rule-response" /></div>
                      <div><label className="text-xs text-gray-600">Remediation Script</label><textarea value={ruleForm.script} onChange={e => setRuleForm({...ruleForm, script: e.target.value})} className="mt-1 w-full h-24 rounded-lg border border-gray-200 px-3 py-2 text-xs font-mono bg-gray-50" placeholder="# PowerShell script..." data-testid="rule-script" /></div>
                      <div><label className="text-xs text-gray-600">Confidence</label><select value={ruleForm.confidence} onChange={e => setRuleForm({...ruleForm, confidence: e.target.value})} className="mt-1 h-9 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="rule-confidence"><option value="high">High (auto-resolve immediately)</option><option value="medium">Medium (requires approval)</option></select></div>
                    </div>
                  )}
                  {ruleForm.rule_type === 'scada' && (
                    <div className="space-y-3">
                      <div><label className="text-xs text-gray-600">Alert Types (comma separated) *</label><Input value={ruleForm.alert_types} onChange={e => setRuleForm({...ruleForm, alert_types: e.target.value})} className="mt-1 rounded-lg" placeholder="motor_overload, vibration_high" data-testid="rule-alerts" /></div>
                      <div><label className="text-xs text-gray-600">Auto-Action *</label><Input value={ruleForm.auto_action} onChange={e => setRuleForm({...ruleForm, auto_action: e.target.value})} className="mt-1 rounded-lg" placeholder="Motor speed reduced to 50%. Maintenance alert triggered." /></div>
                      <div><label className="text-xs text-gray-600">Script</label><textarea value={ruleForm.script} onChange={e => setRuleForm({...ruleForm, script: e.target.value})} className="mt-1 w-full h-24 rounded-lg border border-gray-200 px-3 py-2 text-xs font-mono bg-gray-50" placeholder="# SCADA command script..." /></div>
                    </div>
                  )}
                  {ruleForm.rule_type === 'sysmon' && (
                    <div className="space-y-3">
                      <div><label className="text-xs text-gray-600">IOC Pattern *</label><Input value={ruleForm.ioc_pattern} onChange={e => setRuleForm({...ruleForm, ioc_pattern: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g. certutil download, rundll32 suspicious" data-testid="rule-ioc" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-xs text-gray-600">Action</label><select value={ruleForm.action} onChange={e => setRuleForm({...ruleForm, action: e.target.value})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white"><option value="alert">Alert</option><option value="block_process">Block Process</option><option value="isolate_host">Isolate Host</option><option value="block_connection">Block Connection</option></select></div>
                        <div><label className="text-xs text-gray-600">Min Severity</label><select value={ruleForm.severity_threshold} onChange={e => setRuleForm({...ruleForm, severity_threshold: e.target.value})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>
                      </div>
                      <div><label className="text-xs text-gray-600">Script</label><textarea value={ruleForm.script} onChange={e => setRuleForm({...ruleForm, script: e.target.value})} className="mt-1 w-full h-24 rounded-lg border border-gray-200 px-3 py-2 text-xs font-mono bg-gray-50" placeholder="# Response script..." /></div>
                    </div>
                  )}
                  <div className="flex gap-2 mt-4"><Button onClick={saveCustomRule} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-rule"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save Rule</Button><Button variant="outline" onClick={() => setShowRuleForm(false)} className="rounded-lg text-xs">Cancel</Button></div>
                </div>
              )}

              {/* Custom Rules List */}
              {customRules.length > 0 && (
                <div className="bg-[#D4A843]/5 border border-[#D4A843]/30 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-[#D4A843] uppercase tracking-wider mb-3">Custom Rules ({customRules.length})</h3>
                  <div className="space-y-2">{customRules.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200" data-testid={`custom-rule-${r.id}`}>
                      <div>
                        <p className="text-sm font-medium text-gray-900 flex items-center gap-2">{r.name} <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.rule_type === 'it' ? 'bg-blue-100 text-blue-700' : r.rule_type === 'scada' ? 'bg-orange-100 text-orange-700' : 'bg-violet-100 text-violet-700'}`}>{r.rule_type.toUpperCase()}</span></p>
                        <p className="text-[10px] text-gray-400">{r.keywords?.join(', ') || r.alert_types?.join(', ') || r.ioc_pattern} | By {r.created_by_name}</p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 text-[10px]" onClick={() => deleteCustomRule(r.id)} data-testid={`delete-rule-${r.id}`}><XCircle className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}</div>
                </div>
              )}

              {/* Built-in Rules (existing display) */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Built-in IT Rules ({rules.it_rules?.filter(r => r.builtin !== false).length})</h3>
                <div className="space-y-2">
                  {rules.it_rules?.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`rule-${r.id}`}>
                      <div>
                        <p className="text-sm font-medium text-gray-900 flex items-center gap-1">{r.name || r.category} {r.builtin === false && <span className="text-[10px] text-[#D4A843] font-bold">CUSTOM</span>}</p>
                        <p className="text-[10px] text-gray-500">Keywords: {r.keywords.slice(0, 4).join(', ')}{r.keywords.length > 4 ? ` +${r.keywords.length - 4}` : ''}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.confidence}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">SCADA Rules</h3>
                  {rules.scada_rules?.map((r, i) => (
                    <div key={r.id || i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <p className="text-xs text-gray-700 capitalize flex items-center gap-1">{r.name || (r.id || '').replace(/_/g, ' ')} {r.builtin === false && <span className="text-[10px] text-[#D4A843] font-bold">CUSTOM</span>}</p>
                      <span className="text-[10px] text-gray-400">{r.alert_types.join(', ')}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Sysmon Rules</h3>
                  {rules.sysmon_rules?.map((r, i) => (
                    <div key={`item-${i}`} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <p className="text-xs text-gray-700 flex items-center gap-1">{r.name || r.ioc_pattern} {r.builtin === false && <span className="text-[10px] text-[#D4A843] font-bold">CUSTOM</span>}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">{r.action}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.severity_threshold === 'critical' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>{r.severity_threshold}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* WEBHOOKS */}
          {view === 'webhooks' && (
            <div className="space-y-4" data-testid="rem-webhooks">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Bell className="w-4 h-4 text-[#D4A843]" /> Webhook Notifications</h2>
              {webhookConfig ? (
                <div className="space-y-4">
                  {/* Slack */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Slack</h3>
                      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={webhookConfig.slack_enabled || false} onChange={e => setWebhookConfig({...webhookConfig, slack_enabled: e.target.checked})} className="rounded" data-testid="slack-toggle" /> Enabled</label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><label className="text-xs text-gray-600">Webhook URL</label><Input value={webhookConfig.slack_webhook_url || ''} onChange={e => setWebhookConfig({...webhookConfig, slack_webhook_url: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" placeholder="https://hooks.slack.com/services/..." data-testid="slack-url" /></div>
                      <div><label className="text-xs text-gray-600">Channel</label><Input value={webhookConfig.slack_channel || '#alerts'} onChange={e => setWebhookConfig({...webhookConfig, slack_channel: e.target.value})} className="mt-1 rounded-lg" placeholder="#alerts" data-testid="slack-channel" /></div>
                    </div>
                  </div>

                  {/* Teams */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Microsoft Teams</h3>
                      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={webhookConfig.teams_enabled || false} onChange={e => setWebhookConfig({...webhookConfig, teams_enabled: e.target.checked})} className="rounded" data-testid="teams-toggle" /> Enabled</label>
                    </div>
                    <div><label className="text-xs text-gray-600">Connector URL</label><Input value={webhookConfig.teams_webhook_url || ''} onChange={e => setWebhookConfig({...webhookConfig, teams_webhook_url: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" placeholder="https://outlook.office.com/webhook/..." data-testid="teams-url" /></div>
                  </div>

                  {/* Email */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Email</h3>
                      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={webhookConfig.email_enabled || false} onChange={e => setWebhookConfig({...webhookConfig, email_enabled: e.target.checked})} className="rounded" data-testid="email-toggle" /> Enabled</label>
                    </div>
                    <div><label className="text-xs text-gray-600">Recipients (comma separated)</label><Input value={(webhookConfig.email_recipients || []).join(', ')} onChange={e => setWebhookConfig({...webhookConfig, email_recipients: e.target.value.split(',').map(r => r.trim()).filter(Boolean)})} className="mt-1 rounded-lg" placeholder="admin@eigoo.com, security@eigoo.com" data-testid="email-recipients" /></div>
                  </div>

                  {/* Notification Preferences */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Notify On</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { key: 'notify_on_escalation', label: 'Escalations' },
                        { key: 'notify_on_approval_needed', label: 'Approvals Needed' },
                        { key: 'notify_on_sla_breach', label: 'SLA Breaches' },
                        { key: 'notify_on_security_incident', label: 'Security Incidents' },
                        { key: 'notify_on_auto_resolve', label: 'Auto-Resolutions' },
                      ].map(p => (
                        <label key={p.key} className="flex items-center gap-2 text-xs text-gray-700 p-2 bg-gray-50 rounded-lg cursor-pointer"><input type="checkbox" checked={webhookConfig[p.key] || false} onChange={e => setWebhookConfig({...webhookConfig, [p.key]: e.target.checked})} className="rounded" />{p.label}</label>
                      ))}
                    </div>
                    <div className="mt-3"><label className="text-xs text-gray-600">Minimum Tier to Notify</label><select value={webhookConfig.min_tier_notify || 'L2'} onChange={e => setWebhookConfig({...webhookConfig, min_tier_notify: e.target.value})} className="mt-1 h-9 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="min-tier"><option value="L1">L1 (All)</option><option value="L2">L2+ (Human Review)</option><option value="L3">L3+ (Specialist)</option><option value="L4">L4 Only (Management)</option></select></div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={saveWebhookConfig} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="save-webhooks"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save Configuration</Button>
                    <Button onClick={testWebhook} variant="outline" className="rounded-lg text-xs" data-testid="test-webhook"><Play className="w-3.5 h-3.5 mr-1" /> Send Test</Button>
                  </div>

                  {/* Webhook Logs */}
                  {webhookLogs.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Delivery Logs</h3>
                      {webhookLogs.slice(0, 10).map(l => (
                        <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div><p className="text-xs text-gray-900">{l.title}</p><p className="text-[10px] text-gray-400">{l.event_type} | {l.tier || 'N/A'}</p></div>
                          <div className="flex items-center gap-2"><span className="text-[10px] text-emerald-600 font-medium">{l.sent_to?.join(', ')}</span><span className="text-[10px] text-gray-400">{new Date(l.timestamp).toLocaleString()}</span></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
            </div>
          )}

          {/* SMS NOTIFICATIONS */}
          {view === 'sms' && (
            <div className="space-y-5" data-testid="rem-sms">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#D4A843]" /> SMS Notifications (Twilio)</h2>
                {smsStatus && (
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${smsStatus.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {smsStatus.configured ? `Connected ${smsStatus.phone_number || ''}` : 'Not Configured'}
                  </span>
                )}
              </div>

              {/* Connection Info */}
              {smsStatus && smsStatus.configured && smsStatus.account_info && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs text-emerald-800">Twilio account: <span className="font-bold">{smsStatus.account_info.friendly_name || 'Connected'}</span> | Status: <span className="font-bold">{smsStatus.account_info.status || 'active'}</span> | From: <span className="font-mono font-bold">{smsStatus.phone_number}</span></p>
                </div>
              )}

              {/* Test SMS */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Send className="w-3.5 h-3.5 text-[#D4A843]" /> Test SMS</h3>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="text-xs text-gray-600">Phone Number (E.164 format)</label>
                    <Input value={testPhone} onChange={e => setTestPhone(e.target.value)} className="mt-1 rounded-lg" placeholder="+1234567890" data-testid="test-phone" />
                  </div>
                  <Button onClick={testSms} disabled={sendingSms} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="test-sms-btn">
                    {sendingSms ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send Test
                  </Button>
                </div>
              </div>

              {/* Alert Configuration */}
              {smsConfig && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="sms-config">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Settings className="w-3.5 h-3.5 text-gray-500" /> Alert Configuration</h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                      <input type="checkbox" checked={smsConfig.enabled || false} onChange={e => setSmsConfig({...smsConfig, enabled: e.target.checked})} className="rounded" data-testid="sms-enabled" />
                      Enable automated SMS alerts
                    </label>

                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-2">Alert Triggers</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { key: 'alert_on_sla_breach', label: 'SLA Breaches' },
                          { key: 'alert_on_security_incident', label: 'Security Incidents' },
                          { key: 'alert_on_l4_escalation', label: 'L4 Escalations (Management)' },
                          { key: 'alert_on_l3_escalation', label: 'L3 Escalations (Specialist)' },
                          { key: 'alert_on_approval_needed', label: 'Approvals Needed' },
                        ].map(opt => (
                          <label key={opt.key} className="flex items-center gap-2 text-xs text-gray-700 p-2 bg-gray-50 rounded-lg cursor-pointer">
                            <input type="checkbox" checked={smsConfig[opt.key] || false} onChange={e => setSmsConfig({...smsConfig, [opt.key]: e.target.checked})} className="rounded" />{opt.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-600">On-Call Numbers (comma separated)</label>
                        <Input value={(smsConfig.on_call_numbers || []).join(', ')} onChange={e => setSmsConfig({...smsConfig, on_call_numbers: e.target.value.split(',').map(n => n.trim()).filter(Boolean)})} className="mt-1 rounded-lg font-mono text-xs" placeholder="+12025551234, +12025555678" data-testid="oncall-numbers" />
                        <p className="text-[10px] text-gray-400 mt-1">Technicians on rotation — all alerts</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Admin Numbers (comma separated)</label>
                        <Input value={(smsConfig.admin_numbers || []).join(', ')} onChange={e => setSmsConfig({...smsConfig, admin_numbers: e.target.value.split(',').map(n => n.trim()).filter(Boolean)})} className="mt-1 rounded-lg font-mono text-xs" placeholder="+12025559999" data-testid="admin-numbers" />
                        <p className="text-[10px] text-gray-400 mt-1">Management — L3/L4, SLA, security only</p>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs text-gray-700 mb-2">
                        <input type="checkbox" checked={smsConfig.respect_quiet_hours || false} onChange={e => setSmsConfig({...smsConfig, respect_quiet_hours: e.target.checked})} className="rounded" /> Respect quiet hours (suppress non-critical)
                      </label>
                      {smsConfig.respect_quiet_hours && (
                        <div className="flex gap-3 items-center ml-5">
                          <div>
                            <label className="text-[10px] text-gray-500">Start (UTC)</label>
                            <Input value={smsConfig.quiet_hours_start || '22:00'} onChange={e => setSmsConfig({...smsConfig, quiet_hours_start: e.target.value})} className="mt-0.5 rounded-lg w-24" type="time" />
                          </div>
                          <span className="text-xs text-gray-400 pt-4">to</span>
                          <div>
                            <label className="text-[10px] text-gray-500">End (UTC)</label>
                            <Input value={smsConfig.quiet_hours_end || '07:00'} onChange={e => setSmsConfig({...smsConfig, quiet_hours_end: e.target.value})} className="mt-0.5 rounded-lg w-24" type="time" />
                          </div>
                        </div>
                      )}
                    </div>

                    <Button onClick={saveSmsConfig} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-sms-config">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save SMS Config
                    </Button>
                  </div>
                </div>
              )}

              {/* SMS Delivery Log */}
              {smsLogs.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="sms-logs">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">SMS Delivery Log</h3>
                  <div className="space-y-2">
                    {smsLogs.map(l => (
                      <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="text-xs text-gray-900 flex items-center gap-1.5">
                            {l.results?.every(r => r.success) ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${l.type === 'test' ? 'bg-blue-100 text-blue-700' : l.type === 'auto_alert' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{l.type?.replace('_', ' ')}</span>
                            {l.message?.slice(0, 60)}...
                          </p>
                          <p className="text-[10px] text-gray-400 ml-4.5">To: {l.recipients?.join(', ')} | {l.sent_by === 'system' ? 'Auto' : `By: ${l.sent_by_name || l.sent_by}`}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">{new Date(l.sent_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TEST ENGINE */}
          {view === 'test' && (
            <div className="space-y-4" data-testid="rem-test">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1"><Play className="w-3.5 h-3.5 text-[#D4A843]" /> Test Smart-Resolve Engine</h3>
                <p className="text-xs text-gray-500 mb-4">Enter a ticket title and description to see how the engine would handle it.</p>
                <div className="space-y-3">
                  <div><label className="text-xs text-gray-600">Ticket Title</label><Input value={testTitle} onChange={e => setTestTitle(e.target.value)} placeholder="e.g. VPN not connecting, printer offline, ransomware detected..." className="mt-1 rounded-lg" data-testid="test-title" /></div>
                  <div><label className="text-xs text-gray-600">Description (optional)</label><Input value={testDesc} onChange={e => setTestDesc(e.target.value)} placeholder="Additional details..." className="mt-1 rounded-lg" data-testid="test-desc" /></div>
                  <div className="flex items-end gap-3">
                    <div><label className="text-xs text-gray-600">Priority</label><select value={testPriority} onChange={e => setTestPriority(e.target.value)} className="mt-1 h-9 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="test-priority"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>
                    <Button onClick={runSmartResolve} disabled={testing} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="test-run">
                      {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Run Smart-Resolve
                    </Button>
                  </div>
                </div>
              </div>

              {testResult && (
                <div className={`border rounded-lg p-5 animate-fade-in ${testResult.action === 'auto_resolved' ? 'bg-emerald-50 border-emerald-200' : testResult.action === 'pending_approval' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="test-result">
                  <div className="flex items-center gap-2 mb-3">
                    {testResult.action === 'auto_resolved' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                    {testResult.action === 'pending_approval' && <Clock className="w-5 h-5 text-amber-600" />}
                    {testResult.action === 'escalated' && <ArrowUpRight className="w-5 h-5 text-red-600" />}
                    <span className={`text-sm font-bold ${testResult.action === 'auto_resolved' ? 'text-emerald-700' : testResult.action === 'pending_approval' ? 'text-amber-700' : 'text-red-700'}`}>
                      {testResult.action === 'auto_resolved' ? 'Auto-Resolved' : testResult.action === 'pending_approval' ? 'Sent to Approval Queue' : `Escalated to ${testResult.tier}`}
                    </span>
                    {testResult.confidence && <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${testResult.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{testResult.confidence}</span>}
                  </div>
                  {testResult.response && <p className="text-sm text-gray-700 mb-2">{testResult.response}</p>}
                  {testResult.proposed_action && <p className="text-sm text-gray-700 mb-2">{testResult.proposed_action}</p>}
                  {testResult.reason && <p className="text-sm text-gray-700 mb-2">{testResult.reason}</p>}
                  {testResult.category && <p className="text-[10px] text-gray-500">Category: {testResult.category}</p>}
                  {testResult.script && <pre className="mt-2 text-[10px] bg-gray-900 text-emerald-400 p-3 rounded-lg overflow-x-auto max-h-32 font-mono">{testResult.script}</pre>}
                </div>
              )}
            </div>
          )}

          {/* ANALYTICS */}
          {view === 'analytics' && (
            <div className="space-y-5" data-testid="rem-analytics">
              {/* Time Range Selector */}
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#D4A843]" /> Remediation Analytics</h2>
                <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-0.5">
                  {[7, 14, 30, 90].map(d => (
                    <button key={d} onClick={() => { setAnalyticsDays(d); loadAnalytics(d); }} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${analyticsDays === d ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'text-gray-500 hover:bg-gray-100'}`} data-testid={`analytics-range-${d}`}>{d}d</button>
                  ))}
                </div>
              </div>

              {!analytics ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : (
                <>
                  {/* KPI Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-kpis">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift">
                      <p className="text-2xl font-bold text-[#D4A843]">{analytics.summary?.auto_resolve_rate || 0}%</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Auto-Resolve Rate</p>
                      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#D4A843] rounded-full transition-all" style={{ width: `${Math.min(analytics.summary?.auto_resolve_rate || 0, 100)}%` }} /></div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center hover-lift">
                      <p className="text-2xl font-bold text-emerald-700">{analytics.summary?.total_actions || 0}</p>
                      <p className="text-[10px] text-emerald-600 mt-0.5">Total Actions</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift">
                      <p className="text-2xl font-bold text-gray-900">{analytics.summary?.avg_resolve_time_min || 0}<span className="text-sm font-normal text-gray-400">m</span></p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Avg Resolve Time</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover-lift">
                      <p className="text-2xl font-bold text-gray-900">{analytics.summary?.total_tickets || 0}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Total Tickets</p>
                    </div>
                    <div className={`rounded-lg p-4 text-center hover-lift ${(analytics.summary?.escalation_rate || 0) > 20 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                      <p className={`text-2xl font-bold ${(analytics.summary?.escalation_rate || 0) > 20 ? 'text-red-700' : 'text-gray-900'}`}>{analytics.summary?.escalation_rate || 0}%</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Escalation Rate</p>
                    </div>
                    <div className="bg-[#D4A843]/10 border border-[#D4A843]/30 rounded-lg p-4 text-center hover-lift">
                      <p className="text-2xl font-bold text-[#D4A843]">{analytics.summary?.rules_active || 0}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Active Rules</p>
                    </div>
                  </div>

                  {/* Action Breakdown Mini Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><Zap className="w-4 h-4 text-blue-600" /></div>
                      <div><p className="text-lg font-bold text-gray-900">{analytics.summary?.it_remediations || 0}</p><p className="text-[10px] text-gray-500">IT Remediations</p></div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center"><Activity className="w-4 h-4 text-orange-600" /></div>
                      <div><p className="text-lg font-bold text-gray-900">{analytics.summary?.scada_remediations || 0}</p><p className="text-[10px] text-gray-500">SCADA Actions</p></div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center"><Shield className="w-4 h-4 text-violet-600" /></div>
                      <div><p className="text-lg font-bold text-gray-900">{analytics.summary?.sysmon_responses || 0}</p><p className="text-[10px] text-gray-500">Sysmon Responses</p></div>
                    </div>
                  </div>

                  {/* Daily Trend Chart */}
                  {analytics.daily_trend?.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="analytics-trend-chart">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-[#D4A843]" /> Daily Resolution Trend</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={analytics.daily_trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                          <defs>
                            <linearGradient id="gradResolved" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradReview" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#d97706" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradEscalated" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => v.slice(5)} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                          <Area type="monotone" dataKey="auto_resolved" name="Auto-Resolved" stroke="#059669" fill="url(#gradResolved)" strokeWidth={2} />
                          <Area type="monotone" dataKey="needs_review" name="Needs Review" stroke="#d97706" fill="url(#gradReview)" strokeWidth={2} />
                          <Area type="monotone" dataKey="escalated" name="Escalated" stroke="#dc2626" fill="url(#gradEscalated)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Category Breakdown */}
                    {analytics.category_breakdown?.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="analytics-categories">
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-[#D4A843]" /> Category Breakdown</h3>
                        <ResponsiveContainer width="100%" height={Math.max(180, analytics.category_breakdown.length * 36)}>
                          <BarChart data={analytics.category_breakdown} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={120} />
                            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="auto_resolved" name="Auto-Resolved" fill="#059669" radius={[0, 4, 4, 0]} barSize={14} />
                            <Bar dataKey="count" name="Total" fill="#e2e8f0" radius={[0, 4, 4, 0]} barSize={14} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Escalation Tier Distribution */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="analytics-tiers">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><ArrowUpRight className="w-3.5 h-3.5 text-red-500" /> Escalation Distribution</h3>
                      {analytics.escalation_tiers && (
                        <>
                          <div className="grid grid-cols-4 gap-2 mb-4">
                            {Object.entries(analytics.escalation_tiers).map(([tier, count]) => (
                              <div key={tier} className="text-center p-2 rounded-lg bg-gray-50">
                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold mb-1 ${TIER_COLORS[tier]}`}>{tier}</span>
                                <p className="text-lg font-bold text-gray-900">{count}</p>
                                <p className="text-[10px] text-gray-400">{TIER_NAMES[tier]}</p>
                              </div>
                            ))}
                          </div>
                          {analytics.escalation_triggers && Object.keys(analytics.escalation_triggers).length > 0 && (
                            <div>
                              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-2">Escalation Triggers</p>
                              <div className="space-y-1.5">
                                {Object.entries(analytics.escalation_triggers).map(([trigger, count]) => {
                                  const maxTrigger = Math.max(...Object.values(analytics.escalation_triggers), 1);
                                  return (
                                    <div key={trigger} className="flex items-center gap-2">
                                      <span className="text-[10px] text-gray-600 w-32 truncate capitalize">{trigger.replace(/_/g, ' ')}</span>
                                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#D4A843]/70 rounded-full transition-all" style={{ width: `${(count / maxTrigger) * 100}%` }} /></div>
                                      <span className="text-[10px] font-bold text-gray-700 w-6 text-right">{count}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {analytics.summary?.total_escalations === 0 && <p className="text-center py-6 text-[10px] text-gray-400">No escalations in this period</p>}
                    </div>
                  </div>

                  {/* Rule Effectiveness Table */}
                  {analytics.rule_effectiveness?.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="analytics-rules">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Settings className="w-3.5 h-3.5 text-gray-500" /> Rule Effectiveness</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Rule</th>
                              <th className="text-left py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Category</th>
                              <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Fires</th>
                              <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Auto-Resolved</th>
                              <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Review</th>
                              <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Effectiveness</th>
                              <th className="text-center py-2 px-3 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Confidence</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.rule_effectiveness.map((r, i) => (
                              <tr key={r.rule_id || i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`rule-eff-${r.rule_id}`}>
                                <td className="py-2.5 px-3 font-medium text-gray-900 capitalize">{(r.rule_id || '').replace(/_/g, ' ')}</td>
                                <td className="py-2.5 px-3 text-gray-600">{r.category}</td>
                                <td className="py-2.5 px-3 text-center font-bold text-gray-900">{r.fires}</td>
                                <td className="py-2.5 px-3 text-center text-emerald-600 font-bold">{r.auto_resolved}</td>
                                <td className="py-2.5 px-3 text-center text-amber-600">{r.needs_review}</td>
                                <td className="py-2.5 px-3">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${r.effectiveness_pct >= 80 ? 'bg-emerald-500' : r.effectiveness_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${r.effectiveness_pct}%` }} /></div>
                                    <span className={`font-bold ${r.effectiveness_pct >= 80 ? 'text-emerald-600' : r.effectiveness_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{r.effectiveness_pct}%</span>
                                  </div>
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.avg_confidence_score >= 80 ? 'bg-emerald-100 text-emerald-700' : r.avg_confidence_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{r.avg_confidence_score}%</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {(analytics.summary?.total_actions === 0 && analytics.summary?.total_tickets === 0) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                      <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No remediation data yet for the last {analyticsDays} days.</p>
                      <p className="text-xs text-gray-400 mt-1">Run some tickets through the Smart-Resolve engine to generate analytics.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* REPORTS */}
          {view === 'reports' && (
            <div className="space-y-5" data-testid="rem-reports">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Mail className="w-4 h-4 text-[#D4A843]" /> Scheduled Email Reports</h2>
                {emailStatus && (
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${emailStatus.sendgrid_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {emailStatus.sendgrid_configured ? 'SendGrid Connected' : 'SendGrid Not Configured'}
                  </span>
                )}
              </div>

              {/* SendGrid Status Banner */}
              {emailStatus && !emailStatus.sendgrid_configured && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs text-amber-800 font-medium">Email delivery requires SendGrid. Add <code className="bg-amber-100 px-1.5 py-0.5 rounded text-[10px]">SENDGRID_API_KEY</code> and <code className="bg-amber-100 px-1.5 py-0.5 rounded text-[10px]">SENDER_EMAIL</code> to your backend .env file.</p>
                  <p className="text-[10px] text-amber-600 mt-1">You can still preview and download HTML reports without SendGrid.</p>
                </div>
              )}

              {/* Send Report Now */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Send className="w-3.5 h-3.5 text-[#D4A843]" /> Send Report Now</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600">Recipients (comma separated)</label>
                    <Input value={reportRecipients} onChange={e => setReportRecipients(e.target.value)} className="mt-1 rounded-lg" placeholder="admin@eigoo.com, team@eigoo.com" data-testid="report-recipients" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Period</label>
                    <select value={reportDays} onChange={e => setReportDays(Number(e.target.value))} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="report-days">
                      <option value={7}>Last 7 days (Weekly)</option>
                      <option value={14}>Last 14 days</option>
                      <option value={30}>Last 30 days (Monthly)</option>
                      <option value={90}>Last 90 days (Quarterly)</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={sendReport} disabled={sendingReport} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="send-report-btn">
                    {sendingReport ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send Email
                  </Button>
                  <Button onClick={previewReport} variant="outline" className="rounded-lg text-xs" data-testid="preview-report-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                  {reportPreview && (
                    <Button onClick={downloadReport} variant="outline" className="rounded-lg text-xs" data-testid="download-report-btn">
                      <Download className="w-3.5 h-3.5 mr-1" /> Download HTML
                    </Button>
                  )}
                </div>
              </div>

              {/* Report Preview */}
              {reportPreview && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="report-preview">
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Report Preview</span>
                    <button onClick={() => setReportPreview(null)} className="text-gray-400 hover:text-gray-600"><XCircle className="w-4 h-4" /></button>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto">
                    <iframe title="Report Preview" srcDoc={reportPreview} className="w-full h-[480px] border-0" sandbox="" />
                  </div>
                </div>
              )}

              {/* Schedule Configuration */}
              {reportSchedule && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="report-schedule">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#D4A843]" /> Automated Schedule</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                      <input type="checkbox" checked={reportSchedule.enabled || false} onChange={e => setReportSchedule({...reportSchedule, enabled: e.target.checked})} className="rounded" data-testid="schedule-enabled" />
                      Enable automated report delivery
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-600">Frequency</label>
                        <select value={reportSchedule.frequency || 'weekly'} onChange={e => setReportSchedule({...reportSchedule, frequency: e.target.value})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="schedule-frequency">
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      {reportSchedule.frequency === 'weekly' && (
                        <div>
                          <label className="text-xs text-gray-600">Day of Week</label>
                          <select value={reportSchedule.day_of_week || 'monday'} onChange={e => setReportSchedule({...reportSchedule, day_of_week: e.target.value})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="schedule-day">
                            {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                              <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-gray-600">Time (UTC)</label>
                        <Input value={reportSchedule.time_utc || '08:00'} onChange={e => setReportSchedule({...reportSchedule, time_utc: e.target.value})} className="mt-1 rounded-lg" type="time" data-testid="schedule-time" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Lookback Period</label>
                      <select value={reportSchedule.days_lookback || 7} onChange={e => setReportSchedule({...reportSchedule, days_lookback: Number(e.target.value)})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="schedule-lookback">
                        <option value={1}>Last 24 hours</option>
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                      </select>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-2">Include in Report</p>
                      <div className="flex gap-4 flex-wrap">
                        {[
                          { key: 'include_rules', label: 'Rule Effectiveness' },
                          { key: 'include_escalations', label: 'Escalation Summary' },
                          { key: 'include_categories', label: 'Category Breakdown' },
                        ].map(opt => (
                          <label key={opt.key} className="flex items-center gap-1.5 text-xs text-gray-700">
                            <input type="checkbox" checked={reportSchedule[opt.key] !== false} onChange={e => setReportSchedule({...reportSchedule, [opt.key]: e.target.checked})} className="rounded" />{opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <Button onClick={saveSchedule} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-schedule">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save Schedule
                    </Button>
                  </div>
                </div>
              )}

              {/* Delivery History */}
              {reportLogs.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="report-logs">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Delivery History</h3>
                  <div className="space-y-2">
                    {reportLogs.map(l => (
                      <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="text-xs text-gray-900 flex items-center gap-1.5">
                            {l.success ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                            {l.subject || `${l.days}-day report`}
                          </p>
                          <p className="text-[10px] text-gray-400 ml-4.5">To: {l.recipients?.join(', ')} | By: {l.sent_by_name}</p>
                        </div>
                        <span className="text-[10px] text-gray-400">{new Date(l.sent_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* HISTORY */}
          {view === 'history' && (
            <div className="space-y-4" data-testid="rem-history">
              {!history ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : (
                <>
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">IT Remediations ({history.it?.length || 0})</h3>
                    {history.it?.slice(0, 10).map(r => (
                      <div key={r.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div><p className="text-xs text-gray-900">{r.category}</p><p className="text-[10px] text-gray-400">{r.auto_response?.slice(0, 80)}...</p></div>
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.status === 'auto_resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status?.replace(/_/g, ' ')}</span>
                          <span className="text-[10px] text-gray-400">{new Date(r.resolved_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                    {(!history.it || history.it.length === 0) && <p className="text-[10px] text-gray-400">No IT remediations yet</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">SCADA ({history.scada?.length || 0})</h3>
                      {history.scada?.slice(0, 5).map(r => (
                        <div key={r.id} className="py-2 border-b border-gray-100 last:border-0"><p className="text-xs text-gray-900">{r.alert_type} — {r.device_id}</p><p className="text-[10px] text-gray-400">{r.auto_action?.slice(0, 60)}...</p></div>
                      ))}
                      {(!history.scada || history.scada.length === 0) && <p className="text-[10px] text-gray-400">No SCADA remediations</p>}
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Sysmon ({history.sysmon?.length || 0})</h3>
                      {history.sysmon?.slice(0, 5).map(r => (
                        <div key={r.id} className="py-2 border-b border-gray-100 last:border-0"><p className="text-xs text-gray-900">{r.hostname} — {r.severity}</p><p className="text-[10px] text-gray-400">{r.actions_taken?.length || 0} actions taken</p></div>
                      ))}
                      {(!history.sysmon || history.sysmon.length === 0) && <p className="text-[10px] text-gray-400">No Sysmon responses</p>}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
